import React, { useState, useEffect, useContext, useCallback, createContext } from 'react';

import { getAccessTokenLS } from 'src/utils/localStorageUtils';

import { WebSocketService } from '../services/websocket-service';

import type { ConnectionStatus } from '../services/websocket-service';

interface WebSocketContextValue {
  ws: WebSocketService;
  status: ConnectionStatus;
  subscribe: (eventType: string, handler: (...args: any[]) => void) => () => void;
  send: (data: any) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  url: string;
  children: React.ReactNode;
}

const checkAuth = () => {
  const token = getAccessTokenLS();
  if (!token) return null;

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    const tenantId = decoded.tenantId || decoded.tenant_id;

    if (token && tenantId) {
      return { token, tenantId };
    }
  } catch (e) {
    console.error('Failed to decode token for WebSocket', e);
  }
  return null;
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ url, children }) => {
  const [ws] = useState(() => new WebSocketService({ url }));
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');

  // Initialize with current auth if available for faster connection on refresh
  const [connectionParams, setConnectionParams] = useState<{
    token: string;
    tenantId: string;
  } | null>(() => checkAuth());

  useEffect(() => {
    const interval = setInterval(() => {
      const params = checkAuth();

      // If params found and different from current, update
      if (
        params &&
        (params.token !== connectionParams?.token || params.tenantId !== connectionParams?.tenantId)
      ) {
        setConnectionParams(params);
      } else if (!params && connectionParams) {
        setConnectionParams(null);
        ws.disconnect();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [ws, connectionParams]);

  // Handle immediate logout signal
  useEffect(() => {
    const handleLogout = (event: MessageEvent) => {
      if (event.data?.type === 'DISCONNECT_SOCKET') {
        // console.log('🔌 Immediate WebSocket disconnect received');
        setConnectionParams(null);
        ws.disconnect();
      }
    };
    window.addEventListener('message', handleLogout);
    return () => window.removeEventListener('message', handleLogout);
  }, [ws]);

  // Initial connect if params exist - ONLY when params change or status is disconnected/error
  useEffect(() => {
    // If no auth params, only disconnect if it's NOT a public connection (e.g., sign-up flow)
    if (!connectionParams) {
      if (ws.getStatus() !== 'disconnected' && !ws.isPublic) {
        ws.disconnect();
      }
      return;
    }

    // Update config with new params
    ws.updateConfig({
      token: connectionParams.token,
      tenantId: connectionParams.tenantId,
    });

    // Only connect if disconnected
    // ws.connect() has its own guards to prevent multiple simultaneous connections
    if (status === 'disconnected') {
      // console.log('🔌 Initiating WebSocket connection to tenant:', connectionParams.tenantId);
      ws.connect();
    }
  }, [connectionParams, ws, status]);

  // Subscribe to tenant room on connect
  useEffect(() => {
    const handleConnected = () => {
      if (connectionParams?.tenantId) {
        // console.log('📡 Subscribing to tenant room:', connectionParams.tenantId);
        ws.subscribeToTenant(connectionParams.tenantId);
      }
    };

    const unsubscribe = ws.subscribe('connected', handleConnected);

    // Initial check in case it's already connected
    if (ws.getStatus() === 'connected' && connectionParams?.tenantId) {
      handleConnected();
    }

    return unsubscribe;
  }, [ws, connectionParams]);

  useEffect(() => {
    // Listen for status changes
    const unsubscribe = ws.subscribe('status_change', setStatus);

    // Disconnect on unmount
    return () => {
      unsubscribe();
      ws.disconnect();
    };
  }, [ws]);

  const subscribe = useCallback(
    (eventType: string, handler: (...args: any[]) => void) => ws.subscribe(eventType, handler),
    [ws]
  );

  const send = useCallback(
    (data: any) => {
      ws.send(data);
    },
    [ws]
  );

  const value: WebSocketContextValue = {
    ws,
    status,
    subscribe,
    send,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
